import cx from 'classnames';
import { useState } from 'react';

import {
  Modal,
  NavigationBar,
  SettingsGroup,
  SettingsGroupSwitchItem,
} from '@sb/design-system';
import type { RestoreConfirmationInput, RestoreData } from '@sb/feathers-types';

import { RestoreAlert } from './RestoreAlert';
import type { RestoreBackupOffer } from './RestoreBackupFirstPanel';
import { RestoreBackupFirstPanel } from './RestoreBackupFirstPanel';
import { RestoreCheckpointPanel } from './RestoreCheckpointPanel';
import { RestoreConfirmationActions } from './RestoreConfirmationActions';
import { RestoreDifferencesPanel } from './RestoreDifferencesPanel';
import { RestoreFollowUpPanel } from './RestoreFollowUpPanel';
import { RestoreModelReportPanel } from './RestoreModelReportPanel';
import { RestoreModelSelectionPanel } from './RestoreModelSelectionPanel';
import { RestoreProgressPanel } from './RestoreProgressPanel';

/**
 * Why "Erase and restore" is unavailable, in the order the operator has to
 * deal with them. Undefined once it is pressable.
 *
 * A greyed-out destructive button with no stated reason reads as broken —
 * this is what the button's tooltip says.
 */
export function restoreConfirmBlockedReason({
  isBusy,
  busyReason,
  isBlocked,
  hasAcknowledged,
}: {
  isBusy: boolean;
  busyReason?: string;
  isBlocked: boolean;
  hasAcknowledged: boolean;
}): string | undefined {
  if (isBusy) {
    return `${busyReason ?? 'The robot is busy'}. Stop it before restoring.`;
  }

  if (isBlocked) {
    return 'The robot will refuse this restore: the backup came from a different arm model.';
  }

  if (!hasAcknowledged) {
    return 'Turn on “I understand this cannot be undone” first.';
  }

  return undefined;
}

export interface RestoreConfirmationModalProps {
  data: RestoreData;
  isSubmitting: boolean;
  submitError: string | null;
  onSubmit: (input: RestoreConfirmationInput) => void;
  /** Offered only while the robot is busy — see the note on blocking below.
   *  Absent means there is no way out of this modal. */
  onSnooze?: () => void;
  /** The cloud backup this modal offers before the erase. */
  backup: RestoreBackupOffer;
  /** Dismisses the finished restore and opens a settings section. */
  onOpenSection: (url: string) => void;
}

/**
 * The local interlock on a cloud-ordered restore.
 *
 * Deliberately blocking: it has no close affordance, ignores Escape and
 * backdrop clicks, and covers whatever page the operator was on. A restore
 * erases this robot's database and file areas, and the only person who can
 * tell that the backup belongs to a different arm, a different build, or a
 * different mode is standing in front of it. The cost is stated openly in
 * the PR: an unattended robot no longer completes a restore on its own.
 *
 * With ONE exception. While the robot is busy the restore cannot be
 * confirmed anyway, and this modal covers the whole screen — including the
 * stop controls the operator would need to make it idle. Blocking there
 * would trap them in front of a running robot with no way to act, so a busy
 * robot gets a "Not now" instead. It comes straight back once the robot
 * stops.
 */
export function RestoreConfirmationModal({
  data,
  isSubmitting,
  submitError,
  onSubmit,
  onSnooze,
  backup,
  onOpenSection,
}: RestoreConfirmationModalProps) {
  const { commandId } = data;

  const trainingRuns = data.checkpoint?.trainingRuns ?? [];

  // Default to everything the backup names: a restore is meant to put the
  // robot back the way it was, and deselecting is the deliberate act.
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    trainingRuns.map((run) => run.trainingRunId),
  );

  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  // Reset on the COMMAND, not on the polled payload. This modal re-renders
  // every few seconds off a fresh `data` object, and resetting on anything
  // derived from it wipes the operator's choices out from under them mid
  // decision. A different command is the only thing that invalidates them.
  const [selectionOwner, setSelectionOwner] = useState(commandId);

  if (selectionOwner !== commandId) {
    setSelectionOwner(commandId);
    setSelectedIds(trainingRuns.map((run) => run.trainingRunId));
    setHasAcknowledged(false);
  }

  const isAwaiting = data.state === 'awaitingConfirmation';
  const isDeclined = data.state === 'declined';

  // Answered, engine not started. Normally one phone-home cycle; if
  // updater-bot is dead it never ends, and there is no decision left to
  // offer — so this state gets a way out rather than a blank action bar.
  const isConfirmed = data.state === 'confirmed';
  const isSucceeded = data.state === 'succeeded';

  // A declined restore is over as far as the operator is concerned, so it
  // gets the same dismiss as one that ran. Without it they are held in
  // front of a blocking modal until updater-bot's next phone-home clears
  // the record — and indefinitely if updater-bot is not running.
  const isFinished = isSucceeded || data.state === 'failed' || isDeclined;

  const isBlocked = data.differences.some((d) => d.severity === 'blocking');

  const buildDifference = data.differences.find((d) => d.kind === 'build');

  const { isBusy, reason: busyReason } = data.robotActivity;

  const blockedReason = restoreConfirmBlockedReason({
    isBusy,
    busyReason,
    isBlocked,
    hasAcknowledged,
  });

  return (
    <Modal
      data-testid="restore-confirmation-modal"
      isOpen
      // No close path on purpose — the operator answers, or the command
      // expires on the robot and is reported to the cloud as a failure.
      onClose={() => {}}
      // Fixed, and the same box as the software update modal: this dialog
      // walks itself from confirmation to running to finished while the
      // operator watches it, and sizing to content made it jump between
      // every one of those states.
      className={cx('tw-h-[620px]', 'tw-w-512', 'tw-flex', 'tw-flex-col')}
    >
      <NavigationBar>Restore from backup</NavigationBar>

      <div
        className={cx(
          'tw-flex-1',
          // A flex child defaults to min-height:auto, which lets it grow past
          // the modal instead of scrolling. Without this the overflow-auto
          // below is inert and the content is simply clipped.
          'tw-min-h-0',
          'tw-overflow-auto',
          'tw-overscroll-contain',
          'tw-flex',
          'tw-flex-col',
          'tw-gap-16',
          'tw-px-16',
          'tw-pb-16',
        )}
      >
        {isAwaiting && (
          <p
            className={cx('tw-text-13', 'tw-text-label-secondary', 'tw-px-16')}
            data-testid="restore-confirmation-intro"
          >
            A restore has been ordered for this robot from the cloud. It{' '}
            <b className="tw-text-red">
              erases this robot&apos;s database, configuration, files and code
              blocks
            </b>{' '}
            and replaces them with the backup below. It cannot be undone, and
            nothing happens until you confirm it here.
          </p>
        )}

        {data.checkpoint && (
          <RestoreCheckpointPanel checkpoint={data.checkpoint} />
        )}

        {/* Only while the operator can still act on them — once the apply
            has run, the differences are history and would push the actual
            result below the fold. */}
        {isAwaiting && (
          <RestoreDifferencesPanel differences={data.differences} />
        )}

        {isAwaiting && (
          <RestoreBackupFirstPanel backup={backup} disabled={isSubmitting} />
        )}

        {isAwaiting && (
          <RestoreModelSelectionPanel
            trainingRuns={trainingRuns}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            modelDownloadsEnabled={data.local.modelDownloadsEnabled}
            operationMode={data.local.operationMode}
            disabled={isSubmitting}
          />
        )}

        {!isAwaiting && <RestoreProgressPanel data={data} />}

        {data.modelReport && (
          <RestoreModelReportPanel report={data.modelReport} />
        )}

        {isSucceeded && (
          <RestoreFollowUpPanel
            onOpenSection={onOpenSection}
            disabled={isSubmitting}
          />
        )}

        {/* A restore only has a start time while awaiting if an earlier
            attempt wrote one and then died without an outcome. Saying so
            matters: the intro above promises nothing has happened yet, and
            on this path something already has. */}
        {isAwaiting && data.startedAt && (
          <RestoreAlert
            severity="critical"
            data-testid="restore-confirmation-stalled-banner"
          >
            An earlier attempt started and stopped without finishing, so this
            robot may be part way through a restore. Confirming starts it again
            from the beginning.
          </RestoreAlert>
        )}

        {isAwaiting && isBusy && (
          <RestoreAlert data-testid="restore-confirmation-busy-banner">
            {busyReason ?? 'The robot is busy'}. Stop it before restoring — this
            cannot run while the robot is under command.
          </RestoreAlert>
        )}

        {submitError && (
          <RestoreAlert
            severity="critical"
            data-testid="restore-confirmation-error-banner"
          >
            {submitError}
          </RestoreAlert>
        )}
      </div>

      {/* Outside the scroller on purpose: this toggle gates the primary
          action, and below the fold it reads as a dead end — the operator
          sees a disabled "Erase and restore" with no visible reason. */}
      {isAwaiting && (
        <div className="tw-px-16 tw-pb-8 tw-shrink-0">
          <SettingsGroup>
            <SettingsGroupSwitchItem
              data-testid="restore-confirmation-acknowledge-switch"
              label="I understand this cannot be undone"
              checked={hasAcknowledged}
              disabled={isSubmitting || isBusy}
              onChange={(e) => setHasAcknowledged(e.target.checked)}
            />
          </SettingsGroup>
        </div>
      )}

      {commandId && (
        <RestoreConfirmationActions
          commandId={commandId}
          isAwaiting={isAwaiting}
          isFinished={isFinished}
          buildDifference={buildDifference}
          dismissLabel={isDeclined ? 'Close' : 'Done'}
          isBlocked={isBlocked || isBusy}
          blockedReason={blockedReason}
          hasAcknowledged={hasAcknowledged}
          onSnooze={
            (isAwaiting && isBusy) || isConfirmed ? onSnooze : undefined
          }
          snoozeLabel={isConfirmed ? 'Hide' : 'Not now'}
          isSubmitting={isSubmitting}
          selectedTrainingRunIds={selectedIds}
          onSubmit={onSubmit}
        />
      )}
    </Modal>
  );
}
